import { create } from 'zustand'
import type { Call, Device } from '@twilio/voice-sdk'
import type {
  CallStatus,
  InternalCallStatus,
  DeviceStatus,
  CallStore,
  QueuedCall,
  HeldCall,
  IncomingInternalCall,
  InternalCallItem,
  LocalCallHistoryItem,
  ResumedCallInfo,
  PendingTransfer,
} from '../types'

// ringing中の通話がポーリング結果に連続して存在しなかった回数の閾値。
// 1回の欠落では落とさない (DynamoDB GSI の結果整合性で一時的に空が返ることがあるため)。
// 閾値に達したら WebSocket イベント欠落 (PCスリープ中の call.removed/call.answered 消失等)
// とみなして ringing → idle へ回復する。
const RINGING_SERVER_MISS_THRESHOLD = 2

/**
 * 通話状態管理ストア
 * アプリケーション全体で通話状態を共有
 */
export const useCallStore = create<CallStore>((set, get) => ({
      // ========================================
      // 通話状態
      // ========================================

      // デバイス状態
      deviceStatus: 'initializing' as DeviceStatus,
      deviceError: null,

  // 通話状態
  callStatus: 'idle' as CallStatus,
  ringAudioEnabled: false,
  lastAnsweredCallId: null,

  // Wake Lock
  wakeLock: null,

  // 現在の通話情報
  activeCall: null,
  incomingCall: null,

      // 通話メタデータ
      currentCallId: null,
      currentCallSid: null,
      currentCallHistoryId: null,
      callerNumber: null,
      customerName: null,
      phoneNumberName: null,
      callStartTime: null,
      callDuration: 0,

      // オーディオ状態
      isMuted: false,
      isSpeakerOn: true,

      // Twilio Device インスタンス（参照用）
      twilioDevice: null,

      // setupCallListeners関数（useTwilioDeviceから登録される）
      setupCallListeners: null,

      // ========================================
      // 通話履歴
      // ========================================

      callHistory: [],

      // ========================================
      // 着信キュー
      // ========================================

      queuedCalls: [],
      ignoredCallIds: new Set<string>(),
      ringingServerMissCount: 0,

      // ========================================
      // 保留関連
      // ========================================

      holdQueueName: null,
      isOnHold: false,
      heldCalls: [],
      isWaitingForResumedCall: false,
      resumedCallInfo: null,
      pendingTransfer: null,

      // ========================================
      // 内線通話関連
      // ========================================

      incomingInternalCall: null,
      internalCallStatus: 'idle' as InternalCallStatus,
      currentInternalCallId: null,
      internalCallPeerUserId: null,
      activeInternalCalls: [],

  // ========================================
  // アクション: デバイス状態
  // ========================================

  setDeviceStatus: (status: DeviceStatus, error: string | null = null) => set({
    deviceStatus: status,
    deviceError: error,
  }),

  setTwilioDevice: (device: Device | null) => set({ twilioDevice: device }),
  setSetupCallListeners: (fn: ((call: Call, callId?: string | null) => void) | null) => set({ setupCallListeners: fn }),
  setCurrentCallId: (callId: string | null) => set({ currentCallId: callId }),
  setCurrentCallSid: (callSid: string | null) => set({ currentCallSid: callSid }),
  setCurrentCallHistoryId: (callHistoryId: string | null) => set({ currentCallHistoryId: callHistoryId }),
  clearCurrentCallHistoryId: () => set({ currentCallHistoryId: null }),
  setCallerNumber: (number: string | null) => set({ callerNumber: number }),
  setCustomerName: (name: string | null) => set({ customerName: name }),
  setPhoneNumberName: (name: string | null) => set({ phoneNumberName: name }),

  // 受付開始（着信音ON + Wake Lock獲得）
  enableRingAudio: async () => {
    set({ ringAudioEnabled: true })
    await get().requestWakeLock()
  },

  // 受付開始/終了を切り替え
  toggleRingAudio: async () => {
    const newState = !get().ringAudioEnabled
    set({ ringAudioEnabled: newState })

    if (newState) {
      // 受付開始 → Wake Lock獲得
      await get().requestWakeLock()
    } else {
      // 受付終了 → Wake Lock解放
      await get().releaseWakeLock()
    }
  },

  markCallAnswered: (callId: string) => set({ lastAnsweredCallId: callId }),

  // ========================================
  // Wake Lock管理
  // ========================================

  // Wake Lockを獲得
  requestWakeLock: async () => {
    // Wake Lock APIが利用可能かチェック
    if (!('wakeLock' in navigator)) {
      console.warn('Wake Lock APIはこのブラウザではサポートされていません')
      return
    }

    try {
      // 既存のWake Lockがあれば解放
      const currentLock = get().wakeLock
      if (currentLock) {
        await currentLock.release()
      }

      // 新しいWake Lockを獲得
      const wakeLock = await navigator.wakeLock.request('screen')
      console.log('Wake Lock獲得成功')

      // Wake Lockが解放された時のイベントリスナー
      wakeLock.addEventListener('release', () => {
        console.log('Wake Lockが解放されました')
      })

      set({ wakeLock })
    } catch (err) {
      console.error('Wake Lock獲得エラー:', err)
      // エラーは無視（Wake Lockは必須機能ではないため）
    }
  },

  // Wake Lockを解放
  releaseWakeLock: async () => {
    const wakeLock = get().wakeLock

    if (!wakeLock) {
      return
    }

    try {
      await wakeLock.release()
      console.log('Wake Lock解放完了')
      set({ wakeLock: null })
    } catch (err) {
      console.error('Wake Lock解放エラー:', err)
      set({ wakeLock: null })
    }
  },

  // ========================================
  // アクション: 通話状態
  // ========================================

  setCallStatus: (status: CallStatus) => {
    const prev = get().callStatus
    if (prev !== status) {
      console.log('[callStore] setCallStatus:', prev, '→', status, { timestamp: Date.now() })
      // 状態遷移したらポーリング欠落カウントは仕切り直す
      set({ callStatus: status, ringingServerMissCount: 0 })
      return
    }
    set({ callStatus: status })
  },

  // 着信を受信
  setIncomingCall: (call: Call) => {
    const callerNumber = call?.parameters?.From || '不明'

    set({
      incomingCall: call,
      callStatus: 'ringing',
      callerNumber,
    })
  },

  // 着信に応答
  acceptIncomingCall: () => {
    const { incomingCall } = get()

    if (!incomingCall) {
      console.warn('応答する着信がありません')
      return
    }

    set({
      activeCall: incomingCall,
      incomingCall: null,
      callStatus: 'connecting',
      callStartTime: Date.now(),
    })

    // Twilio Call を応答
    incomingCall.accept()
  },

  // 着信を拒否
  rejectIncomingCall: () => {
    const { incomingCall } = get()

    if (!incomingCall) {
      console.warn('拒否する着信がありません')
      return
    }

    incomingCall.reject()

    set({
      incomingCall: null,
      callStatus: 'idle',
      callerNumber: null,
    })
  },

  // 通話開始（着信応答後 or 発信後に接続された時）
  startCall: (call: Call, callId: string | null = null) => {
    // 既存のcallerNumberを保持（setCallerNumberで事前に設定されている場合）
    const existingCallerNumber = get().callerNumber
    const prevStatus = get().callStatus

    console.log('[callStore] startCall:', { callId, prevStatus, timestamp: Date.now() })

    set({
      activeCall: call,
      currentCallId: callId,
      callStatus: 'connected',
      callStartTime: Date.now(),
      callDuration: 0,
      isSpeakerOn: true,
      // callerNumberが未設定の場合のみFromから取得
      callerNumber: existingCallerNumber || call?.parameters?.From || null,
    })
  },

  // 発信開始（接続待ち表示用）
  startOutgoingCall: (call: Call, callId: string | null = null) => {
    const existingNumber = get().callerNumber
    const target = existingNumber
      || call?.parameters?.From
      || call?.parameters?.To
      || call?.parameters?.queueName
      || '発信中'
    set({
      activeCall: call,
      currentCallId: callId,
      callerNumber: target,
      callStatus: 'connecting',
      callStartTime: Date.now(),
      callDuration: 0,
      isSpeakerOn: true,
    })
  },

  // 通話終了
  endCall: () => {
    const { activeCall, twilioDevice, callStatus, currentCallId } = get()

    console.log('[callStore] endCall called:', {
      callStatus,
      currentCallId,
      hasActiveCall: !!activeCall,
      timestamp: Date.now(),
      stack: new Error().stack?.split('\n').slice(1, 5).join(' <- '),
    })

    // Twilio Device 経由で常に切断
    if (twilioDevice && typeof twilioDevice.disconnectAll === 'function') {
      twilioDevice.disconnectAll()
    } else if (activeCall && typeof activeCall.disconnect === 'function') {
      activeCall.disconnect()
    }

    // 通話履歴への追加はTwilioデバイスのイベントリスナーで処理

    set({
      activeCall: null,
      incomingCall: null,
      currentCallId: null,
      currentCallSid: null,
      currentCallHistoryId: null,
      callerNumber: null,
      customerName: null,
      phoneNumberName: null,
      callStatus: 'idle',
      callStartTime: null,
      callDuration: 0,
      isMuted: false,
      isSpeakerOn: true,
      // 内線通話の状態もクリア
      incomingInternalCall: null,
      internalCallStatus: 'idle',
      // 着信無視リストもクリア
      ignoredCallIds: new Set<string>(),
    })
  },

  // 内線通話を切断して状態をクリア（保留解除・転送の前処理として使用）
  // 通常のendCallと異なり、holdQueueName等の保留関連状態は保持する
  endInternalCall: () => {
    const { twilioDevice, activeCall } = get()

    // SDK経由で切断
    if (twilioDevice && typeof twilioDevice.disconnectAll === 'function') {
      twilioDevice.disconnectAll()
    } else if (activeCall && typeof activeCall.disconnect === 'function') {
      activeCall.disconnect()
    }

    set({
      activeCall: null,
      currentCallId: null,
      currentInternalCallId: null,
      internalCallPeerUserId: null,
      incomingInternalCall: null,
      internalCallStatus: 'idle',
      callStatus: 'idle',
      callerNumber: null,
    })
  },

  // 接続前のキャンセル（Call.ignoreを使用）
  ignoreCall: () => {
    const { activeCall } = get()

    if (activeCall && typeof (activeCall as Call & { ignore?: () => void }).ignore === 'function') {
      (activeCall as Call & { ignore: () => void }).ignore()
    }

    set({
      activeCall: null,
      incomingCall: null,
      currentCallId: null,
      callerNumber: null,
      callStatus: 'idle',
      callStartTime: null,
      callDuration: 0,
      isMuted: false,
      isSpeakerOn: true,
    })
  },

  // 通話時間を更新（1秒ごとに呼ぶ）
  updateCallDuration: () => {
    const { callStartTime, callStatus } = get()

    if (callStatus === 'connected' && callStartTime) {
      const duration = Math.floor((Date.now() - callStartTime) / 1000)
      set({ callDuration: duration })
    }
  },

  // ========================================
  // アクション: オーディオ制御
  // ========================================

  toggleMute: () => {
    const { activeCall, isMuted } = get()

    if (!activeCall) {
      console.warn('通話中ではありません')
      return
    }

    const newMutedState = !isMuted
    activeCall.mute(newMutedState)

    set({ isMuted: newMutedState })

    return newMutedState
  },

  toggleSpeaker: () => {
    const { activeCall, isSpeakerOn } = get()

    if (!activeCall) {
      console.warn('通話中ではありません')
      return
    }

    const newSpeakerState = !isSpeakerOn

    // Twilio SDKのgetRemoteStream()を使用してスピーカー音量を制御
    try {
      const remoteStream = (activeCall as Call & { getRemoteStream?: () => MediaStream }).getRemoteStream?.()
      if (remoteStream) {
        const audioTracks = remoteStream.getAudioTracks()
        audioTracks.forEach(track => {
          track.enabled = newSpeakerState
        })
      }
    } catch (err) {
      console.warn('スピーカー制御エラー:', err)
    }

    set({ isSpeakerOn: newSpeakerState })

    return newSpeakerState
  },

  // ========================================
  // アクション: 通話履歴管理
  // ========================================

  // 通話履歴に追加
  addCallToHistory: (callData: Partial<LocalCallHistoryItem>) => {
    const { callHistory } = get()
    const newCall: LocalCallHistoryItem = {
      id: callData.callId || `call-${Date.now()}`,
      callId: callData.callId || `call-${Date.now()}`,
      from: callData.from || '不明',
      to: callData.to || '不明',
      status: callData.status || 'connecting',
      duration: callData.duration || 0,
      date: callData.date || Date.now(),
      twilioCallSid: callData.twilioCallSid,
      hasRecording: false,
      direction: callData.direction || 'inbound',
    }

    // 重複チェック（同じCallSidがあれば更新）
    const existingIndex = callHistory.findIndex(
      call => call.twilioCallSid === newCall.twilioCallSid || call.callId === newCall.callId
    )

    if (existingIndex !== -1) {
      // 既存の履歴を更新
      const updatedHistory = [...callHistory]
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        ...newCall,
      }
      set({ callHistory: updatedHistory })
    } else {
      // 新規追加（最新を先頭に）
      set({ callHistory: [newCall, ...callHistory] })
    }
  },

  // 通話履歴のステータスを更新
  updateCallHistoryStatus: (callId: string, status: string, duration: number | null = null) => {
    const { callHistory } = get()
    const updatedHistory = callHistory.map(call => {
      if (call.callId === callId || call.twilioCallSid === callId) {
        return {
          ...call,
          status,
          ...(duration !== null && { duration }),
        }
      }
      return call
    })
    set({ callHistory: updatedHistory })
  },

  // 通話履歴をクリア
  clearCallHistory: () => set({ callHistory: [] }),

  // ========================================
  // アクション: 着信キュー管理
  // ========================================

  // キューイングされた着信を設定
  setQueuedCalls: (calls: QueuedCall[]) => set({ queuedCalls: calls }),

  // キューイングされた着信を追加
  addQueuedCall: (call: QueuedCall) => {
    const { queuedCalls } = get()
    // 重複チェック
    if (queuedCalls.some(c => c.callId === call.callId || (call.twilioCallSid && c.twilioCallSid === call.twilioCallSid))) {
      return
    }
    set({ queuedCalls: [...queuedCalls, call] })
  },

  // キューイングされた着信を削除
  removeQueuedCall: (callId: string) => {
    const { queuedCalls } = get()
    set({ queuedCalls: queuedCalls.filter(c => c.callId !== callId) })
  },

  // 現在の着信がキューに存在するか確認
  isCallStillQueued: (callId: string) => {
    const { queuedCalls } = get()
    return queuedCalls.some(c => c.callId === callId)
  },

  // ========================================
  // イベント駆動キューアクション
  // ========================================

  // WS call.queued イベント: キューに追加し、idle時はringing表示
  handleIncomingQueued: (callData: QueuedCall) => {
    const { queuedCalls, callStatus, currentCallId, ignoredCallIds } = get()

    // 重複チェック
    const existing = queuedCalls.find(c => c.callId === callData.callId)
    if (existing) {
      // 重複でもcustomerName/phoneNumberNameが更新されていれば反映
      if (callData.customerName || callData.phoneNumberName) {
        const updated = queuedCalls.map(c =>
          c.callId === callData.callId
            ? {
                ...c,
                ...(callData.customerName && { customerName: callData.customerName }),
                ...(callData.phoneNumberName && { phoneNumberName: callData.phoneNumberName }),
              }
            : c
        )
        set({ queuedCalls: updated })

        // 現在ringing表示中の通話の名前も更新
        if (callStatus === 'ringing' && currentCallId === callData.callSid) {
          const patch: Partial<{ customerName: string | null; phoneNumberName: string | null }> = {}
          if (callData.customerName) patch.customerName = callData.customerName
          if (callData.phoneNumberName) patch.phoneNumberName = callData.phoneNumberName
          set(patch)
        }
      }
      return
    }

    // 新規追加
    const newQueue = [...queuedCalls, callData]
    const updates: Record<string, unknown> = { queuedCalls: newQueue }

    // idle時 → 無視済みでない先頭をringing表示
    if (callStatus === 'idle') {
      const firstNotIgnored = newQueue.find(c => !ignoredCallIds.has(c.callId))
      if (firstNotIgnored) {
        updates.callStatus = 'ringing'
        updates.callerNumber = firstNotIgnored.from || null
        updates.customerName = firstNotIgnored.customerName || null
        updates.phoneNumberName = firstNotIgnored.phoneNumberName || null
        updates.currentCallId = firstNotIgnored.callSid || firstNotIgnored.callId
        updates.ringingServerMissCount = 0
      }
    }

    set(updates)
  },

  // WS call.answered(他者)/call.removed イベント: キューから削除、必要に応じて次のringing表示
  handleCallGone: (callId: string) => {
    const { queuedCalls, callStatus, currentCallId } = get()

    // キューから削除
    const remaining = queuedCalls.filter(c => c.callId !== callId)
    const updates: Record<string, unknown> = { queuedCalls: remaining, ringingServerMissCount: 0 }

    // connecting/connected中 → キュー削除のみ（状態遷移なし）
    if (callStatus === 'connecting' || callStatus === 'connected') {
      set(updates)
      return
    }

    // 現在ringing中のコールが消えた場合
    // currentCallIdはcallSid（CA...）で設定されるが、callIdはUUIDなので両方チェック
    const isCurrentRinging = callStatus === 'ringing' && (
      currentCallId === callId ||
      queuedCalls.find(c => c.callId === callId)?.callSid === currentCallId
    )

    if (isCurrentRinging) {
      if (remaining.length > 0) {
        // 次のコールをringing表示
        const next = remaining[0]
        updates.callerNumber = next.from || null
        updates.customerName = next.customerName || null
        updates.phoneNumberName = next.phoneNumberName || null
        updates.currentCallId = next.callSid || next.callId
        // callStatus は 'ringing' のまま
      } else {
        // キュー空 → idle
        updates.callStatus = 'idle'
        updates.callerNumber = null
        updates.customerName = null
        updates.phoneNumberName = null
        updates.currentCallId = null
      }
    }

    set(updates)
  },

  // ポーリング結果との同期: サーバーのキューでローカルを置換 + callStatusの矛盾を修正
  // 重要: ringing→idle 遷移は 1 回のポーリング結果では行わない。
  // ポーリングはDynamoDB GSIの結果整合性やグループフィルタのタイミングで
  // 一時的に空を返す場合があるため。
  // ただし RINGING_SERVER_MISS_THRESHOLD 回連続でサーバーに存在しなければ、
  // WebSocketイベント (call.answered/call.removed) の欠落 (PCスリープ等) とみなして
  // idle へ回復する。これがないとイベントを取りこぼした場合に鳴動が永久に止まらない。
  reconcileQueueFromServer: (serverCalls: QueuedCall[]) => {
    const { callStatus, currentCallId, ignoredCallIds, ringingServerMissCount } = get()
    const updates: Record<string, unknown> = { queuedCalls: serverCalls }

    // ignoredを除外したコール（ringing表示の候補）
    const notIgnored = serverCalls.filter(c => !ignoredCallIds.has(c.callId))
    // サーバーに存在しないignoredCallIdをクリーンアップ
    const serverCallIdSet = new Set(serverCalls.map(c => c.callId))
    const cleanedIgnored = new Set([...ignoredCallIds].filter(id => serverCallIdSet.has(id)))
    if (cleanedIgnored.size !== ignoredCallIds.size) {
      updates.ignoredCallIds = cleanedIgnored
    }

    const serverCallSids = new Set(serverCalls.map(c => c.callSid).filter(Boolean))

    // connecting/connected中 → キュー更新のみ
    if (callStatus === 'connecting' || callStatus === 'connected') {
      set(updates)
      return
    }

    // ringing中 → サーバー結果との矛盾を解消
    if (callStatus === 'ringing' && currentCallId) {
      const stillExists = serverCallIdSet.has(currentCallId) || serverCallSids.has(currentCallId)
      if (stillExists) {
        if (ringingServerMissCount !== 0) {
          updates.ringingServerMissCount = 0
        }
      } else if (notIgnored.length > 0) {
        // サーバーに別のコールがある → 次のコールをringing表示
        const next = notIgnored[0]
        updates.callerNumber = next.from || null
        updates.customerName = next.customerName || null
        updates.phoneNumberName = next.phoneNumberName || null
        updates.currentCallId = next.callSid || next.callId
        updates.ringingServerMissCount = 0
      } else {
        // サーバーにコールが1件もない → 欠落カウントを進める。
        // 1回では落とさず (GSI結果整合性対策)、閾値連続で初めて idle へ回復する
        const missCount = ringingServerMissCount + 1
        if (missCount >= RINGING_SERVER_MISS_THRESHOLD) {
          updates.callStatus = 'idle'
          updates.callerNumber = null
          updates.customerName = null
          updates.phoneNumberName = null
          updates.currentCallId = null
          updates.ringingServerMissCount = 0
        } else {
          updates.ringingServerMissCount = missCount
        }
      }
    }

    // idle中だがサーバーに未無視コールあり → ringing表示（WS欠損回復）
    if (callStatus === 'idle' && notIgnored.length > 0) {
      const first = notIgnored[0]
      updates.callStatus = 'ringing'
      updates.callerNumber = first.from || null
      updates.customerName = first.customerName || null
      updates.phoneNumberName = first.phoneNumberName || null
      updates.currentCallId = first.callSid || first.callId
      updates.ringingServerMissCount = 0
    }

    set(updates)
  },

  // 着信を無視（UIの着信表示を消すが、キューからは削除しない）
  // 他のオペレーターが引き続き応答可能
  dismissRingingCall: () => {
    const { callStatus, currentCallId, queuedCalls, ignoredCallIds } = get()

    if (callStatus !== 'ringing') return

    // 現在ringing中のcallIdをignoredに追加
    const currentCall = queuedCalls.find(c =>
      c.callSid === currentCallId || c.callId === currentCallId
    )
    const newIgnored = new Set(ignoredCallIds)
    if (currentCall) {
      newIgnored.add(currentCall.callId)
    }

    // ignoredでないコールを探す
    const remaining = queuedCalls.filter(c => !newIgnored.has(c.callId))

    if (remaining.length > 0) {
      // 次の未無視コールをringing表示
      const next = remaining[0]
      set({
        ignoredCallIds: newIgnored,
        callerNumber: next.from || null,
        customerName: next.customerName || null,
        phoneNumberName: next.phoneNumberName || null,
        currentCallId: next.callSid || next.callId,
        ringingServerMissCount: 0,
      })
    } else {
      // 全て無視済み → idle
      set({
        ignoredCallIds: newIgnored,
        callStatus: 'idle',
        callerNumber: null,
        customerName: null,
        phoneNumberName: null,
        currentCallId: null,
        ringingServerMissCount: 0,
      })
    }
  },

  // ========================================
  // アクション: 保留管理
  // ========================================

  // 保留キュー名を設定
  setHoldQueueName: (queueName: string | null) => set({
    holdQueueName: queueName,
    isOnHold: !!queueName
  }),

  // 保留キュー名をクリア
  clearHoldQueueName: () => set({
    holdQueueName: null,
    isOnHold: false
  }),

  // 保留中の通話一覧を設定
  setHeldCalls: (calls: HeldCall[]) => set({ heldCalls: calls }),

  // 保留中の通話を追加
  addHeldCall: (call: HeldCall) => {
    const { heldCalls } = get()
    // 重複チェック
    if (heldCalls.some(c => c.holdQueueName === call.holdQueueName)) {
      return
    }
    set({ heldCalls: [...heldCalls, call] })
  },

  // 保留中の通話を削除
  removeHeldCall: (holdQueueName: string) => {
    const { heldCalls } = get()
    set({
      heldCalls: heldCalls.filter(c => c.holdQueueName !== holdQueueName)
    })
  },

  // 保留通話の状態を更新（保留中 -> 再開中）
  updateHeldCallStatus: (holdQueueName: string, status: string) => {
    const { heldCalls } = get()
    set({
      heldCalls: heldCalls.map(c =>
        c.holdQueueName === holdQueueName
          ? { ...c, status: status as HeldCall['status'] }
          : c
      )
    })
  },

  // 保留解除後の着信待ち状態を設定
  setWaitingForResumedCall: (callInfo: ResumedCallInfo) => set({
    isWaitingForResumedCall: true,
    resumedCallInfo: callInfo
  }),

  // 保留解除後の着信待ち状態をクリア
  clearWaitingForResumedCall: () => set({
    isWaitingForResumedCall: false,
    resumedCallInfo: null
  }),

  // 転送待ち状態を設定（WebSocketで転送割り当てを受信した時）
  setPendingTransfer: (transfer: PendingTransfer) => set({
    pendingTransfer: transfer,
  }),

  // 転送待ち状態をクリア（device.connect()完了 or エラー時）
  clearPendingTransfer: () => set({
    pendingTransfer: null,
  }),

  // ========================================
  // アクション: 内線通話管理
  // ========================================

  // 内線通話の着信を設定
  setIncomingInternalCall: (internalCall: IncomingInternalCall) => {
    const prev = get().incomingInternalCall || {} as IncomingInternalCall
    const merged: IncomingInternalCall = {
      ...prev,
      ...internalCall,
      // twilioCallは新規が優先。無い場合は既存を保持。
      twilioCall: internalCall.twilioCall || prev.twilioCall || null,
    }
    set({
      incomingInternalCall: merged,
      internalCallStatus: 'ringing',
    })
  },

  // 内線通話をクリア
  clearIncomingInternalCall: () => {
    set({
      incomingInternalCall: null,
    })
  },

  // 内線通話のステータスを更新
  setInternalCallStatus: (status: InternalCallStatus) => {
    set({ internalCallStatus: status })
  },

  // アクティブな内線通話を設定
  setActiveInternalCalls: (calls: InternalCallItem[]) => {
    set({ activeInternalCalls: calls })
  },

  // アクティブな内線通話を追加
  addActiveInternalCall: (call: InternalCallItem) => {
    const { activeInternalCalls } = get()
    if (!activeInternalCalls.some(c => c.internalCallId === call.internalCallId)) {
      set({ activeInternalCalls: [...activeInternalCalls, call] })
    }
  },

  // アクティブな内線通話を削除
  removeActiveInternalCall: (internalCallId: string) => {
    const { activeInternalCalls } = get()
    set({
      activeInternalCalls: activeInternalCalls.filter(c => c.internalCallId !== internalCallId)
    })
  },

  // ========================================
  // ヘルパー: 状態チェック
  // ========================================

  isDeviceReady: () => get().deviceStatus === 'ready',
  isRinging: () => get().callStatus === 'ringing',
  isConnected: () => get().callStatus === 'connected',
  isInCall: () => ['connecting', 'connected'].includes(get().callStatus),

  // ========================================
  // リセット
  // ========================================

  reset: async () => {
    // Wake Lockを解放
    await get().releaseWakeLock()

    // Twilio Deviceを破棄
    const device = get().twilioDevice
    if (device) {
      try {
        device.destroy()
      } catch {
        // 既に破棄済みの場合は無視
      }
    }

    set({
      deviceStatus: 'initializing',
      deviceError: null,
      callStatus: 'idle',
      activeCall: null,
      incomingCall: null,
      currentCallId: null,
      callerNumber: null,
      callStartTime: null,
      callDuration: 0,
      isMuted: false,
      isSpeakerOn: true,
      twilioDevice: null,
      wakeLock: null,
      holdQueueName: null,
      isOnHold: false,
      pendingTransfer: null,
      ignoredCallIds: new Set<string>(),
      ringingServerMissCount: 0,
      // 通話履歴と保留中の通話一覧は保持
    })
  },
}))

// ========================================
// ヘルパー関数: 通話時間フォーマット
// ========================================

export const formatCallDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

// ========================================
// ヘルパー関数: 通話状態の表示テキスト
// ========================================

export const getCallStatusText = (callStatus: string): string => {
  const statusMap: Record<string, string> = {
    idle: '待受中',
    ringing: '着信中',
    connecting: '接続中',
    connected: '通話中',
    disconnected: '切断',
  }

  return statusMap[callStatus] || '不明'
}

// ========================================
// ヘルパー関数: Twilioステータスの表示テキスト
// ========================================

export const getTwilioStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    'queued': '着信中',
    'ringing': '呼び出し中',
    'in-progress': '通話中',
    'completed': '完了',
    'busy': '不達',
    'failed': '通話失敗',
    'no-answer': '応答なし',
    'canceled': 'キャンセル',
    'connecting': '接続中',
    'closed_hours': '営業時間外',
    'connected': '通話中',
  }

  return statusMap[status] || status || '不明'
}

// ========================================
// ヘルパー関数: ステータスの色
// ========================================

export const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'queued': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'ringing': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'in-progress': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'connected': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'completed': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'busy': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    'failed': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    'no-answer': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    'canceled': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    'connecting': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    'closed_hours': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  }

  return colorMap[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
}

// ========================================
// ヘルパー関数: デバイス状態の表示テキスト
// ========================================

export const getDeviceStatusText = (deviceStatus: string): string => {
  const statusMap: Record<string, string> = {
    initializing: '初期化中',
    ready: '準備完了',
    offline: 'オフライン',
    error: 'エラー',
  }

  return statusMap[deviceStatus] || '不明'
}
